// ============================================
// CONSTANTES DE UMBRALES DE SCORING
// ============================================

export const UMBRALES_SCORE = {
  PERFECTO: 90, // score >= 90 → Auto-conciliable
  BUENO: 70, // score >= 70 → Sugerido
  DEBIL: 30, // score >= 30 → Débil (requiere revisión)
  // score < 30 → Sin match real
};

// ============================================
// FUNCIÓN PRINCIPAL DE PROCESAMIENTO
// ============================================

/**
 * Procesa y clasifica los resultados del procesamiento automático
 *
 * @param {Object} resultados - Objeto con resultados del backend
 * @param {Array} resultados.detalle - Array de movimientos procesados
 * @param {Number} resultados.total_procesados - Total de movimientos procesados
 * @param {String} resultados.tiempo_proceso - Tiempo de procesamiento (opcional)
 *
 * @returns {Object} Objeto con clasificados, métricas y porCategoria
 */
export const procesarDatosResultados = (resultados) => {
  // Validación de entrada
  if (
    !resultados ||
    !resultados.detalle ||
    !Array.isArray(resultados.detalle)
  ) {
    return crearEstructuraVacia();
  }

  // Inicializar contenedores de clasificación
  const clasificados = {
    perfectos: [],
    buenos: [],
    debiles: [],
    sinMatch: [],
    errores: [],
  };

  // Variables para métricas globales
  let montoTotal = 0;
  let sumScores = 0;
  let countConMatch = 0;

  // Clasificar cada movimiento
  resultados.detalle.forEach((detalle) => {
    const monto = detalle.movimiento?.monto || 0;
    montoTotal += monto;

    // Errores van a su propia categoría
    if (detalle.status === "ERROR") {
      clasificados.errores.push(detalle);
      return;
    }

    // Clasificar por score
    const categoria = clasificarMovimiento(detalle);
    clasificados[categoria].push(detalle);

    // Acumular scores para promedio (solo movimientos con match)
    if (categoria !== "sinMatch") {
      sumScores += detalle.score || 0;
      countConMatch++;
    }
  });

  // Calcular métricas por categoría
  const porCategoria = {
    perfectos: calcularMetricasCategoria(clasificados.perfectos),
    buenos: calcularMetricasCategoria(clasificados.buenos),
    debiles: calcularMetricasCategoria(clasificados.debiles),
    sinMatch: calcularMetricasCategoriaSinScore(clasificados.sinMatch),
    errores: { cantidad: clasificados.errores.length },
  };

  // Calcular métricas globales
  const totalProcesados =
    resultados.total_procesados || resultados.detalle.length;
  const metricas = calcularMetricasGenerales({
    totalProcesados,
    montoTotal,
    sumScores,
    countConMatch,
    clasificados,
    tiempoProceso: resultados.tiempo_proceso,
  });

  return {
    clasificados,
    metricas,
    porCategoria,
  };
};

// ============================================
// FUNCIONES DE CLASIFICACIÓN
// ============================================

/**
 * Clasifica un movimiento en su categoría según score y status
 *
 * @param {Object} detalle - Detalle del movimiento
 * @returns {String} Categoría: 'perfectos', 'buenos', 'debiles', 'sinMatch'
 */
export const clasificarMovimiento = (detalle) => {
  const score = detalle.score || 0;
  const status = detalle.status;

  // AUTO_CONCILIADO o score >= 90 → Perfectos
  if (status === "AUTO_CONCILIADO" || score >= UMBRALES_SCORE.PERFECTO) {
    return "perfectos";
  }

  // Score 70-89 → Buenos (sugeridos)
  if (score >= UMBRALES_SCORE.BUENO) {
    return "buenos";
  }

  // Score 30-69 con candidatos → Débiles
  if (
    score >= UMBRALES_SCORE.DEBIL &&
    score < UMBRALES_SCORE.BUENO &&
    (detalle.candidatos > 0 ||
      (detalle.facturas && detalle.facturas.length > 0))
  ) {
    return "debiles";
  }

  // Score < 30 o sin candidatos → Sin match
  return "sinMatch";
};

// ============================================
// CÁLCULO DE MÉTRICAS POR CATEGORÍA
// ============================================

/**
 * Calcula métricas para categorías con score (perfectos, buenos, débiles)
 *
 * @param {Array} items - Array de movimientos de la categoría
 * @returns {Object} { cantidad, monto, scorePromedio }
 */
export const calcularMetricasCategoria = (items) => {
  if (!items || items.length === 0) {
    return { cantidad: 0, monto: 0, scorePromedio: 0 };
  }

  const monto = items.reduce(
    (sum, item) => sum + (item.movimiento?.monto || 0),
    0,
  );

  const scoreSum = items.reduce((sum, item) => sum + (item.score || 0), 0);
  const scorePromedio = Math.round(scoreSum / items.length);

  return {
    cantidad: items.length,
    monto,
    scorePromedio,
  };
};

/**
 * Calcula métricas para categorías sin score (sinMatch)
 *
 * @param {Array} items - Array de movimientos sin match
 * @returns {Object} { cantidad, monto }
 */
export const calcularMetricasCategoriaSinScore = (items) => {
  if (!items || items.length === 0) {
    return { cantidad: 0, monto: 0 };
  }

  const monto = items.reduce(
    (sum, item) => sum + (item.movimiento?.monto || 0),
    0,
  );

  return {
    cantidad: items.length,
    monto,
  };
};

// ============================================
// CÁLCULO DE MÉTRICAS GLOBALES
// ============================================

/**
 * Calcula métricas generales del procesamiento
 *
 * @param {Object} params - Parámetros para cálculo
 * @returns {Object} Métricas globales
 */
export const calcularMetricasGenerales = ({
  totalProcesados,
  montoTotal,
  sumScores,
  countConMatch,
  clasificados,
  tiempoProceso,
}) => {
  // Score promedio (solo de movimientos con match)
  const scorePromedio =
    countConMatch > 0 ? Math.round(sumScores / countConMatch) : 0;

  // Tasa de éxito: % de movimientos con match bueno o perfecto (score >= 70)
  const movimientosExitosos =
    clasificados.perfectos.length + clasificados.buenos.length;
  const tasaExito =
    totalProcesados > 0
      ? Math.round((movimientosExitosos / totalProcesados) * 100)
      : 0;

  return {
    totalProcesados,
    montoTotal,
    scorePromedio,
    tasaExito,
    tiempoProceso: tiempoProceso || null,
  };
};

// ============================================
// FUNCIONES DE VALIDACIÓN Y UTILIDAD
// ============================================

/**
 * Determina si estamos en "modo marcha blanca"
 * (Hay matches perfectos pero no auto-conciliados aún)
 *
 * @param {Array} perfectos - Array de movimientos perfectos
 * @returns {Boolean}
 */
export const esModoMarchaBlanca = (perfectos) => {
  return (
    perfectos.length > 0 &&
    perfectos.every((p) => p.status !== "AUTO_CONCILIADO")
  );
};

/**
 * Calcula el porcentaje de una categoría respecto al total
 *
 * @param {Number} cantidad - Cantidad de items en la categoría
 * @param {Number} total - Total de items
 * @returns {Number} Porcentaje con 1 decimal
 */
export const calcularPorcentaje = (cantidad, total) => {
  if (total === 0) return 0;
  return parseFloat(((cantidad / total) * 100).toFixed(1));
};

/**
 * Renderiza preview de facturas con badges de estado (React component)
 * Para usar en FilaMovimiento.js
 *
 * @param {Object} detalle - Detalle del movimiento
 * @returns {JSX.Element|String} Componente React o string
 */
export const renderPreviewFacturasConEstado = (detalle) => {
  const facturas = detalle.facturas || [];

  if (facturas.length === 0) {
    const candidatos = detalle.candidatos || 0;
    if (candidatos > 0) {
      return `${candidatos} ${
        candidatos === 1 ? "candidato disponible" : "candidatos disponibles"
      }`;
    }
    return "Sin facturas asociadas";
  }

  // Si hay 1-2 facturas, retornar array de objetos para renderizado individual
  if (facturas.length <= 2) {
    return facturas;
  }

  // Si hay más de 2, retornar string resumen (sin badges)
  return `${facturas.length} facturas (${facturas[0].tipo} #${
    facturas[0].folio
  }, +${facturas.length - 1} más)`;
};

/**
 * Obtiene la configuración de badge según score
 *
 * @param {Number} score - Score del movimiento
 * @returns {Object} { label, color, bgColor }
 */
export const obtenerConfigBadgeScore = (score) => {
  if (!score || score === 0) {
    return {
      label: "N/A",
      color: "#fff",
      bgColor: "#9e9e9e",
    };
  }

  if (score >= UMBRALES_SCORE.PERFECTO) {
    return {
      label: "PERFECTO",
      color: "#fff",
      bgColor: "#4caf50",
    };
  }

  if (score >= UMBRALES_SCORE.BUENO) {
    return {
      label: "BUENO",
      color: "#fff",
      bgColor: "#ff9800",
    };
  }

  if (score >= UMBRALES_SCORE.DEBIL) {
    return {
      label: "DÉBIL",
      color: "#000",
      bgColor: "#ffeb3b",
    };
  }

  return {
    label: "BAJO",
    color: "#fff",
    bgColor: "#f44336",
  };
};

// ============================================
// HELPERS PARA REGLAS CRÍTICAS
// ============================================

/**
 * Obtiene la configuración de badge para una regla crítica aplicada
 *
 * @param {String} nombreRegla - Nombre de la regla aplicada
 * @returns {Object} { label, color, bgColor, tooltip }
 */
export const obtenerConfigBadgeRegla = (nombreRegla) => {
  const configs = {
    FOLIO_EN_GLOSA_EXPLICITO: {
      label: "Folio en Glosa",
      color: "#2196F3",
      bgColor: "#2196F3",
      tooltip: "Folio encontrado explícitamente en la glosa del pago",
    },
    MONTO_EXACTO_RUT_EXACTO: {
      label: "Match Exacto",
      color: "#4CAF50",
      bgColor: "#4CAF50",
      tooltip: "Monto exacto + RUT exacto del cliente",
    },
    MONTO_EXACTO_RUT_RELACIONADO_VALIDADO: {
      label: "Requiere Validación",
      color: "#FF9800",
      bgColor: "#FF9800",
      tooltip: "Monto exacto + RUT relacionado (requiere validación humana)",
    },
  };

  return (
    configs[nombreRegla] || {
      label: "Regla Aplicada",
      color: "#2196F3",
      bgColor: "#2196F3",
      tooltip: "Regla crítica de negocio aplicada",
    }
  );
};

/**
 * Formatea el nombre de una regla para mostrar de forma legible
 *
 * @param {String} nombreRegla - Nombre de la regla en formato CONSTANT_CASE
 * @returns {String} Nombre formateado en Title Case
 */
export const formatearNombreRegla = (nombreRegla) => {
  if (!nombreRegla) return "";

  return nombreRegla
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Determina si un movimiento/detalle tiene reglas críticas aplicadas
 *
 * @param {Object} detalle - Objeto del detalle del movimiento
 * @returns {Boolean} true si tiene alguna regla aplicada
 */
export const tieneReglasAplicadas = (detalle) => {
  if (!detalle) return false;

  return !!(
    detalle.aplicada_por_regla ||
    detalle.requiere_validacion_humana ||
    detalle.priorizado_por_monto_exacto
  );
};

/**
 * Obtiene el nivel de prioridad de una regla (1 = más alta)
 *
 * @param {String} nombreRegla - Nombre de la regla
 * @returns {Number} Nivel de prioridad (1-3)
 */
export const obtenerPrioridadRegla = (nombreRegla) => {
  const prioridades = {
    FOLIO_EN_GLOSA_EXPLICITO: 1,
    MONTO_EXACTO_RUT_EXACTO: 2,
    MONTO_EXACTO_RUT_RELACIONADO_VALIDADO: 3,
  };

  return prioridades[nombreRegla] || 99;
};

/**
 * Obtiene una descripción extendida de la regla aplicada
 *
 * @param {String} nombreRegla - Nombre de la regla
 * @returns {String} Descripción detallada
 */
export const obtenerDescripcionRegla = (nombreRegla) => {
  const descripciones = {
    FOLIO_EN_GLOSA_EXPLICITO:
      "Esta conciliación fue priorizada porque el folio de la factura se encontró explícitamente en la glosa del pago bancario, lo que indica alta confianza en el match.",

    MONTO_EXACTO_RUT_EXACTO:
      "Match de alta confianza: el monto del pago coincide exactamente con el monto de la factura, y el RUT del ordenante coincide exactamente con el RUT del cliente.",

    MONTO_EXACTO_RUT_RELACIONADO_VALIDADO:
      "El monto coincide exactamente, pero el RUT del ordenante es un RUT relacionado al cliente (ej: sucursal, holding). Se recomienda validar manualmente antes de auto-conciliar.",
  };

  return (
    descripciones[nombreRegla] ||
    "Regla crítica de negocio aplicada al matching automático."
  );
};

/**
 * Determina el ícono apropiado para una regla (nombre de ícono de Material UI)
 *
 * @param {String} nombreRegla - Nombre de la regla
 * @returns {String} Nombre del ícono de Material UI
 */
export const obtenerIconoRegla = (nombreRegla) => {
  const iconos = {
    FOLIO_EN_GLOSA_EXPLICITO: "Receipt",
    MONTO_EXACTO_RUT_EXACTO: "CheckCircle",
    MONTO_EXACTO_RUT_RELACIONADO_VALIDADO: "Warning",
  };

  return iconos[nombreRegla] || "Gavel";
};

// ============================================
// HELPERS PARA DIFERENCIAS DE MONTO / ABONOS
// ============================================

/**
 * Determina si un movimiento tiene diferencia de monto
 *
 * @param {Object} detalle - Objeto del detalle del movimiento
 * @returns {Boolean} true si tiene diferencia de monto
 */
export const tieneDiferenciaMonto = (detalle) => {
  if (!detalle) return false;
  return (
    detalle.tiene_diferencia_monto === true && detalle.monto_diferencia > 0
  );
};

/**
 * Determina si un movimiento requiere gestión de abono
 *
 * @param {Object} detalle - Objeto del detalle del movimiento
 * @returns {Boolean} true si requiere gestión de abono
 */
export const requiereGestionAbono = (detalle) => {
  if (!detalle) return false;
  return detalle.requiere_gestion_abono === true;
};

/**
 * Obtiene la configuración de badge para diferencia de monto
 *
 * @param {String} tipoDiferencia - "EXCESO" o "FALTANTE"
 * @returns {Object} { label, color, bgColor, icon, tooltip }
 */
export const obtenerConfigBadgeDiferencia = (tipoDiferencia) => {
  if (tipoDiferencia === "EXCESO") {
    return {
      label: "Exceso",
      color: "#FFF",
      bgColor: "#FF9800", // Naranja
      icon: "AccountBalanceWallet",
      tooltip: "Hay un exceso de pago que puede aplicarse como abono",
    };
  }

  if (tipoDiferencia === "FALTANTE") {
    return {
      label: "Faltante",
      color: "#FFF",
      bgColor: "#F44336", // Rojo
      icon: "Warning",
      tooltip: "El pago es menor que la suma de facturas",
    };
  }

  return {
    label: "Diferencia",
    color: "#000",
    bgColor: "#FFC107", // Amarillo
    icon: "Info",
    tooltip: "Hay una diferencia en el monto",
  };
};

/**
 * Formatea la información de diferencia para mostrar
 *
 * @param {Object} detalle - Detalle del movimiento
 * @returns {Object|null} { tipoDiferencia, montoDiferencia, sumaFacturas, montoMovimiento, porcentaje }
 */
export const obtenerInfoDiferencia = (detalle) => {
  if (!tieneDiferenciaMonto(detalle)) {
    return null;
  }

  const montoDiferencia = detalle.monto_diferencia || 0;
  const sumaFacturas = detalle.suma_facturas || 0;
  const montoMovimiento =
    detalle.monto_movimiento || detalle.movimiento?.monto || 0;

  // Calcular porcentaje de diferencia
  const porcentaje =
    montoMovimiento > 0
      ? Math.round((montoDiferencia / montoMovimiento) * 100)
      : 0;

  return {
    tipoDiferencia: detalle.tipo_diferencia || "DESCONOCIDO",
    montoDiferencia,
    sumaFacturas,
    montoMovimiento,
    porcentaje,
    requiereAbono: detalle.requiere_gestion_abono || false,
  };
};

/**
 * Obtiene descripción textual de la diferencia
 *
 * @param {Object} detalle - Detalle del movimiento
 * @returns {String} Descripción legible de la diferencia
 */
export const obtenerDescripcionDiferencia = (detalle) => {
  const info = obtenerInfoDiferencia(detalle);

  if (!info) {
    return "";
  }

  const { tipoDiferencia, montoDiferencia, porcentaje } = info;

  if (tipoDiferencia === "EXCESO") {
    return `Exceso de $${montoDiferencia.toLocaleString(
      "es-CL",
    )} (${porcentaje}% del pago)`;
  }

  if (tipoDiferencia === "FALTANTE") {
    return `Faltante de $${montoDiferencia.toLocaleString(
      "es-CL",
    )} (${porcentaje}% del pago)`;
  }

  return `Diferencia: $${montoDiferencia.toLocaleString("es-CL")}`;
};

/**
 * Determina el color de severidad para la diferencia
 *
 * @param {Number} porcentaje - Porcentaje de diferencia
 * @returns {String} Color MUI severity ("success", "warning", "error", "info")
 */
export const obtenerSeveridadDiferencia = (porcentaje) => {
  if (porcentaje <= 5) return "success"; // Diferencia muy pequeña
  if (porcentaje <= 15) return "warning"; // Diferencia moderada
  if (porcentaje <= 30) return "error"; // Diferencia alta
  return "error"; // Diferencia muy alta
};

// ============================================
// ESTRUCTURA VACÍA PARA CASOS SIN DATOS
// ============================================

/**
 * Crea estructura de retorno vacía cuando no hay datos
 *
 * @returns {Object} Estructura vacía
 */
const crearEstructuraVacia = () => ({
  clasificados: {
    perfectos: [],
    buenos: [],
    debiles: [],
    sinMatch: [],
    errores: [],
  },
  metricas: {
    totalProcesados: 0,
    montoTotal: 0,
    scorePromedio: 0,
    tasaExito: 0,
    tiempoProceso: null,
  },
  porCategoria: {
    perfectos: { cantidad: 0, monto: 0, scorePromedio: 0 },
    buenos: { cantidad: 0, monto: 0, scorePromedio: 0 },
    debiles: { cantidad: 0, monto: 0, scorePromedio: 0 },
    sinMatch: { cantidad: 0, monto: 0 },
    errores: { cantidad: 0 },
  },
});
